import { useEffect, useState } from "react";
import api from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Eye, Pencil, ImagePlus, KeyRound, Copy, Check, ListPlus } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import QRPoster from "@/components/QRPoster";
import AdminAddActivityDialog from "@/components/AdminAddActivityDialog";
import FeaturedEventAdmin from "@/components/FeaturedEventAdmin";
import RacePitchEditor from "@/components/RacePitchEditor";
import RaceRosterDialog from "@/components/RaceRosterDialog";

export default function Admin() {
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10" data-testid="admin-page">
      <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">CONTROL ROOM</p>
      <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">Admin</h1>

      <Tabs defaultValue="runners" className="mt-8">
        <TabsList className="bg-zinc-900 rounded-none border border-zinc-800 p-0 h-auto flex-wrap">
          {["runners", "events", "races", "challenges", "tips", "qr", "settings"].map((k) => (
            <TabsTrigger key={k} value={k}
              className="rounded-none data-[state=active]:bg-orange-600 data-[state=active]:text-white font-display tracking-wider px-5 py-2">
              {k.toUpperCase()}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="runners"><RunnersAdmin /></TabsContent>
        <TabsContent value="events"><EventsAdmin /></TabsContent>
        <TabsContent value="races"><RacesAdmin /></TabsContent>
        <TabsContent value="challenges"><ChallengesAdmin /></TabsContent>
        <TabsContent value="tips"><TipsAdmin /></TabsContent>
        <TabsContent value="qr" className="mt-6"><QRPoster /></TabsContent>
        <TabsContent value="settings"><SettingsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function RunnersAdmin() {
  const [runners, setRunners] = useState([]);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cityFilter, setCityFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/runners").then((r) => setRunners(r.data));
  }, []);

  const reloadRunners = () => {
    api.get("/admin/runners").then((r) => setRunners(r.data));
  };

  const cities = Array.from(new Set(runners.map((r) => r.city).filter(Boolean))).sort();
  const visible = runners.filter((r) => {
    if (cityFilter !== "ALL" && r.city !== cityFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!(r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const openRunner = async (uid) => {
    setOpen(uid);
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/runners/${uid}/progress`);
      setDetail(data);
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => { setOpen(null); setDetail(null); };

  // Edit state
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({});
  const startEdit = (r) => {
    setEditing(r.user_id);
    setEdit({
      name: r.name || "", email: r.email || "", city: r.city || "",
      profession: r.profession || "", bio: r.bio || "",
      height_cm: r.height_cm || "", weight_kg: r.weight_kg || "",
      running_history: r.running_history || "", future_goals: r.future_goals || "",
      role: r.role || "runner",
    });
  };
  const saveEdit = async () => {
    try {
      const payload = { ...edit };
      payload.height_cm = payload.height_cm ? Number(payload.height_cm) : null;
      payload.weight_kg = payload.weight_kg ? Number(payload.weight_kg) : null;
      const { data } = await api.put(`/admin/runners/${editing}`, payload);
      setRunners((rs) => rs.map((x) => x.user_id === editing ? { ...x, ...data } : x));
      toast.success(`Updated ${data.name}`);
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const deleteRunner = async (uid, name) => {
    try {
      await api.delete(`/admin/runners/${uid}`);
      toast.success(`Removed ${name}`);
      setRunners((rs) => rs.filter((x) => x.user_id !== uid));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  // ── Reset password (admin-driven) ─────────────────────────────────────
  const [resetTarget, setResetTarget] = useState(null);   // runner row
  const [resetCustom, setResetCustom] = useState("");
  const [resetResult, setResetResult] = useState(null);    // { email, temporary_password }
  const [resetBusy, setResetBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const openReset = (r) => {
    setResetTarget(r);
    setResetCustom("");
    setResetResult(null);
    setCopied(false);
  };
  const closeReset = () => {
    setResetTarget(null);
    setResetCustom("");
    setResetResult(null);
    setCopied(false);
  };
  const doReset = async () => {
    if (!resetTarget) return;
    const custom = resetCustom.trim();
    if (custom && custom.length < 6) {
      toast.error("Custom password must be at least 6 characters");
      return;
    }
    try {
      setResetBusy(true);
      const { data } = await api.post(
        `/admin/runners/${resetTarget.user_id}/reset-password`,
        custom ? { new_password: custom } : {},
      );
      setResetResult(data);
      toast.success(`Password reset for ${data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetBusy(false);
    }
  };
  const copyReset = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <div className="mt-6">
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-xl text-white tracking-wider">RUNNERS · {visible.length}{cityFilter !== "ALL" ? ` IN ${cityFilter.toUpperCase()}` : ""}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            data-testid="runners-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email…"
            className="rounded-none bg-zinc-900 border border-zinc-800 text-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-600 w-56"
          />
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500">CITY</span>
          <select
            data-testid="runners-city-filter"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="rounded-none bg-zinc-900 border border-zinc-800 text-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-600"
          >
            <option value="ALL">All cities</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="hairline bg-zinc-900 divide-y divide-zinc-800 overflow-x-auto">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 font-mono text-[10px] tracking-widest text-zinc-500 uppercase min-w-[800px]">
          <div className="col-span-3">RUNNER</div>
          <div className="col-span-2">CITY</div>
          <div className="col-span-3">EMAIL</div>
          <div className="col-span-1 text-right">KM</div>
          <div className="col-span-1 text-right">CHECK-INS</div>
          <div className="col-span-1">LAST ACTIVE</div>
          <div className="col-span-1 text-right">ACTIONS</div>
        </div>
        {visible.map((r) => (
          <div
            key={r.user_id}
            data-testid={`runner-row-${r.user_id}`}
            className="grid grid-cols-12 gap-3 px-4 py-3 hover:bg-zinc-900/60 transition items-center min-w-[800px]"
          >
            <button onClick={() => openRunner(r.user_id)} className="col-span-3 flex items-center gap-3 text-left">
              {r.picture ? (
                <img src={r.picture} alt="" className="w-8 h-8 object-cover hairline" />
              ) : (
                <div className="w-8 h-8 hairline bg-zinc-800 flex items-center justify-center font-display text-sm text-white">
                  {r.name?.[0]?.toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-white text-sm truncate">{r.name}</p>
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{r.role}</p>
              </div>
            </button>
            <div className="col-span-2 text-xs text-orange-500 truncate">{r.city || "—"}</div>
            <div className="col-span-3 text-xs text-zinc-400 truncate">{r.email}</div>
            <div className="col-span-1 text-right font-display text-orange-500">{r.total_km || 0}</div>
            <div className="col-span-1 text-right font-mono text-xs text-zinc-300">{r.total_checkins || 0}</div>
            <div className="col-span-1 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">{r.last_active || "—"}</div>
            <div className="col-span-1 flex justify-end items-center gap-2">
              <button onClick={() => openRunner(r.user_id)} className="text-zinc-500 hover:text-white p-1" title="View progress" data-testid={`view-runner-${r.user_id}`}>
                <Eye size={14} />
              </button>
              <button onClick={() => startEdit(r)} className="text-zinc-500 hover:text-orange-500 p-1" title="Edit profile" data-testid={`edit-runner-${r.user_id}`}>
                <Pencil size={14} />
              </button>
              <AdminAddActivityDialog runner={r} onChange={reloadRunners}>
                <button
                  className="text-zinc-500 hover:text-orange-500 p-1"
                  title="Add / import activity"
                  data-testid={`add-activity-${r.user_id}`}
                >
                  <ListPlus size={14} />
                </button>
              </AdminAddActivityDialog>
              {r.role !== "admin" && (
                <button
                  onClick={() => openReset(r)}
                  className="text-zinc-500 hover:text-orange-500 p-1"
                  title="Reset password"
                  data-testid={`reset-password-${r.user_id}`}
                >
                  <KeyRound size={14} />
                </button>
              )}
              {r.role !== "admin" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-runner-${r.user_id}`} className="text-zinc-500 hover:text-red-500 p-1" title="Remove runner">
                      <Trash2 size={14} />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-zinc-950 border-zinc-800 rounded-none text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-display tracking-wider">REMOVE RUNNER?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">
                        This will permanently delete <span className="text-orange-500">{r.name}</span> ({r.email})
                        and all their check-ins, posts, activities and RSVPs. This cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider">CANCEL</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid={`confirm-delete-${r.user_id}`}
                        onClick={() => deleteRunner(r.user_id, r.name)}
                        className="rounded-none bg-red-600 hover:bg-red-500 font-display tracking-wider"
                      >
                        DELETE
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="p-8 text-center text-zinc-500 font-mono text-sm">No runners match.</div>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-none text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wider">
              {detail?.user?.name || "Runner"} · <span className="text-orange-500">PROGRESS</span>
            </DialogTitle>
          </DialogHeader>
          {loading || !detail ? (
            <p className="font-mono text-sm text-zinc-500">Loading…</p>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <Mini label="TOTAL KM" value={detail.user.total_km || 0} />
                <Mini label="CHECK-INS" value={detail.total_checkins} />
                <Mini label="BADGES" value={(detail.user.badges || []).length} />
              </div>
              {detail.by_challenge.length === 0 && (
                <p className="font-mono text-sm text-zinc-500">No check-ins yet.</p>
              )}
              {detail.by_challenge.map((c) => (
                <div key={c.challenge_id} className="hairline bg-zinc-900 p-4">
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="font-mono text-[10px] tracking-widest text-orange-500 uppercase">
                        {c.start_date} → {c.end_date}
                      </p>
                      <h4 className="font-display text-lg text-white tracking-wider">{c.title}</h4>
                    </div>
                    <span className="font-display text-2xl text-orange-500">
                      {c.checkins.length}/{c.target_days || "?"}
                    </span>
                  </div>
                  <div className="mt-3 divide-y divide-zinc-800">
                    {c.checkins.map((ck) => (
                      <div key={ck.checkin_id || ck.date} className="py-2 flex items-start gap-3 text-sm">
                        <div className="font-mono text-[11px] text-zinc-400 tracking-widest w-24 shrink-0">{ck.date}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white">
                            {ck.distance_km ? `${ck.distance_km} km` : "—"}
                            {ck.notes && <span className="text-zinc-400 ml-2">· {ck.notes}</span>}
                          </div>
                          {ck.strava_url && (
                            <a href={ck.strava_url} target="_blank" rel="noopener noreferrer" className="text-orange-500 text-xs hover:underline break-all">
                              {ck.strava_url}
                            </a>
                          )}
                          {ck.photo_base64 && (
                            <img src={ck.photo_base64} alt="" className="mt-2 max-h-32 hairline" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* ACTIVITIES — admin can delete suspicious entries */}
              <div className="hairline bg-zinc-900 p-4">
                <div className="flex items-end justify-between mb-2">
                  <h4 className="font-display text-lg text-white tracking-wider">
                    ACTIVITIES <span className="font-mono text-xs text-zinc-500 ml-1">{(detail.activities || []).length}</span>
                  </h4>
                  <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">Trash any suspicious entry</p>
                </div>
                {(!detail.activities || detail.activities.length === 0) && (
                  <p className="font-mono text-sm text-zinc-500">No activities logged.</p>
                )}
                <div className="divide-y divide-zinc-800">
                  {(detail.activities || []).map((a) => (
                    <div key={a.activity_id} className="py-2 flex items-start gap-3 text-sm">
                      <div className="font-mono text-[11px] text-zinc-400 tracking-widest w-24 shrink-0">{a.date}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white">
                          {a.distance_km || 0} km
                          {a.duration_min ? ` · ${a.duration_min} min` : ""}
                          <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{a.activity_type}</span>
                          {a.source === "strava" && (
                            <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-orange-500">via STRAVA</span>
                          )}
                        </div>
                        {a.notes && <p className="text-zinc-400 text-xs mt-0.5">{a.notes}</p>}
                        {a.strava_url && (
                          <a href={a.strava_url} target="_blank" rel="noopener noreferrer" className="text-orange-500 text-xs hover:underline break-all">{a.strava_url}</a>
                        )}
                        {a.photo_base64 && <img src={a.photo_base64} alt="" className="mt-2 max-h-32 hairline" />}
                      </div>
                      <button
                        data-testid={`admin-delete-activity-${a.activity_id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete this activity (${a.date} · ${a.distance_km || 0} km)? This cannot be undone.`)) return;
                          try {
                            await api.delete(`/activities/${a.activity_id}`);
                            toast.success("Activity deleted");
                            setDetail((d) => ({ ...d, activities: d.activities.filter((x) => x.activity_id !== a.activity_id) }));
                          } catch {
                            toast.error("Delete failed");
                          }
                        }}
                        className="text-zinc-500 hover:text-red-500 p-1 shrink-0"
                        title="Delete this activity"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* RESET PASSWORD DIALOG */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && closeReset()}>
        <DialogContent
          data-testid="reset-password-dialog"
          className="bg-zinc-950 border-zinc-800 rounded-none text-white max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wider flex items-center gap-2">
              <KeyRound size={18} className="text-orange-500" />
              RESET PASSWORD
            </DialogTitle>
          </DialogHeader>
          {!resetResult ? (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                For <span className="text-white">{resetTarget?.name}</span>{" "}
                <span className="text-zinc-500">({resetTarget?.email})</span>.
                Leave the field blank to auto-generate a memorable temporary password like{" "}
                <span className="font-mono text-orange-500">OSCR-ABC-123</span>.
              </p>
              <div>
                <label className="font-display tracking-widest text-xs text-zinc-400">
                  CUSTOM PASSWORD (OPTIONAL · MIN 6 CHARS)
                </label>
                <Input
                  data-testid="reset-password-custom-input"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Leave blank to auto-generate"
                  value={resetCustom}
                  onChange={(e) => setResetCustom(e.target.value)}
                  className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  onClick={closeReset}
                  variant="outline"
                  className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider"
                >
                  CANCEL
                </Button>
                <Button
                  data-testid="reset-password-confirm"
                  onClick={doReset}
                  disabled={resetBusy}
                  className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider"
                >
                  {resetBusy ? "RESETTING…" : "RESET PASSWORD"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="hairline bg-orange-600/10 border-orange-600/40 p-4">
                <p className="font-mono text-[10px] tracking-widest uppercase text-orange-500">
                  NEW PASSWORD FOR {resetResult.email}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <code
                    data-testid="reset-password-value"
                    className="font-mono text-2xl text-white select-all flex-1 break-all"
                  >
                    {resetResult.temporary_password}
                  </code>
                  <button
                    data-testid="reset-password-copy"
                    onClick={copyReset}
                    className="p-2 text-zinc-400 hover:text-orange-500"
                    title="Copy password"
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">
                Share this with the runner over WhatsApp or phone. This screen is the only place
                it will appear — the password is stored as a bcrypt hash. Ask the runner to log in
                and change it from their profile page.
              </p>
              <div className="flex justify-end">
                <Button
                  data-testid="reset-password-close"
                  onClick={closeReset}
                  className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider"
                >
                  DONE
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-none text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-wider">
              EDIT RUNNER · <span className="text-orange-500">{edit.name}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <FieldInput label="NAME" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} dt="edit-name" />
            <FieldInput label="EMAIL" value={edit.email} onChange={(v) => setEdit({ ...edit, email: v })} dt="edit-email" />
            <div className="grid grid-cols-2 gap-3">
              <FieldInput label="CITY" value={edit.city} onChange={(v) => setEdit({ ...edit, city: v })} dt="edit-city" />
              <FieldInput label="PROFESSION" value={edit.profession} onChange={(v) => setEdit({ ...edit, profession: v })} dt="edit-profession" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldInput type="number" label="HEIGHT (CM)" value={edit.height_cm} onChange={(v) => setEdit({ ...edit, height_cm: v })} dt="edit-height" />
              <FieldInput type="number" label="WEIGHT (KG)" value={edit.weight_kg} onChange={(v) => setEdit({ ...edit, weight_kg: v })} dt="edit-weight" />
            </div>
            <FieldTextarea label="BIO" value={edit.bio} onChange={(v) => setEdit({ ...edit, bio: v })} dt="edit-bio" rows={2} />
            <FieldTextarea label="RUNNING HISTORY" value={edit.running_history} onChange={(v) => setEdit({ ...edit, running_history: v })} dt="edit-history" rows={2} />
            <FieldTextarea label="FUTURE GOALS" value={edit.future_goals} onChange={(v) => setEdit({ ...edit, future_goals: v })} dt="edit-goals" rows={2} />
            <div>
              <label className="font-display tracking-widest text-xs text-zinc-400">ROLE</label>
              <select data-testid="edit-role" value={edit.role}
                onChange={(e) => setEdit({ ...edit, role: e.target.value })}
                className="mt-2 w-full rounded-none bg-zinc-950 border border-zinc-800 text-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-600">
                <option value="runner">runner</option>
                <option value="admin">admin</option>
              </select>
            </div>
            <Button data-testid="edit-save-btn" onClick={saveEdit}
              className="w-full rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider py-5">
              SAVE CHANGES
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsAdmin() {
  const [s, setS] = useState({
    instagram_oscr: "", instagram_founder: "", whatsapp_url: "", strava_club_url: "", youtube_url: "",
    news_items: [], gallery_images: [], instagram_posts: [], featured_event: null
  });
  const [newsText, setNewsText] = useState("");
  const [instaPostsText, setInstaPostsText] = useState("");
  const [galleryDraft, setGalleryDraft] = useState({ url: "", caption: "" });
  useEffect(() => {
    api.get("/settings").then((r) => {
      setS({ ...s, ...r.data }); // eslint-disable-line
      setNewsText((r.data?.news_items || []).join("\n"));
      setInstaPostsText((r.data?.instagram_posts || []).join("\n"));
    });
    // eslint-disable-next-line
  }, []);
  const save = async () => {
    try {
      const items = newsText.split("\n").map((x) => x.trim()).filter(Boolean);
      const igPosts = instaPostsText.split("\n").map((x) => x.trim()).filter(
        (u) => /^https?:\/\/(www\.)?instagram\.com\/(p|reel|tv)\//i.test(u),
      );
      await api.put("/settings", {
        ...s,
        news_items: items,
        gallery_images: s.gallery_images,
        instagram_posts: igPosts,
      });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };
  const addGalleryImage = () => {
    const url = (galleryDraft.url || "").trim();
    if (!url) { toast.error("Image URL required"); return; }
    const next = [...(s.gallery_images || []), { url, caption: (galleryDraft.caption || "").trim() }];
    setS({ ...s, gallery_images: next });
    setGalleryDraft({ url: "", caption: "" });
  };
  const removeGalleryImage = (i) => {
    const next = [...(s.gallery_images || [])];
    next.splice(i, 1);
    setS({ ...s, gallery_images: next });
  };
  const moveGalleryImage = (i, dir) => {
    const next = [...(s.gallery_images || [])];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setS({ ...s, gallery_images: next });
  };
  return (
    <div className="mt-6 max-w-2xl">
      <div className="hairline bg-zinc-900 p-5 space-y-3">
        <h3 className="font-display text-xl text-white tracking-wider">NEWS TICKER</h3>
        <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
          One headline per line. Scrolls across every page. Leave blank to hide.
        </p>
        <Textarea
          data-testid="news-items"
          value={newsText}
          onChange={(e) => setNewsText(e.target.value)}
          rows={6}
          placeholder={"Bhubaneswar Half Marathon registrations open\nNew challenge — 100K in October — joins now open\nWeekly long run · Sunday 5 AM · Hanuman Vatika"}
          className="rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600"
        />
      </div>

      {/* FEATURED EVENT SPOTLIGHT */}
      {s.featured_event && (
        <FeaturedEventAdmin value={s.featured_event} onChange={(fe) => setS({ ...s, featured_event: fe })} />
      )}

      {/* INSTAGRAM CURATED FEED */}
      <div className="hairline bg-zinc-900 p-5 space-y-3 mt-6" data-testid="instagram-admin">
        <h3 className="font-display text-xl text-white tracking-wider">INSTAGRAM FEED · LANDING PAGE</h3>
        <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
          Paste OSCR Instagram post/reel URLs — one per line. Shown as an editorial grid on the homepage.
          Formats accepted: <span className="text-orange-500">instagram.com/p/…</span>,{" "}
          <span className="text-orange-500">/reel/…</span>, <span className="text-orange-500">/tv/…</span>.
          Invalid lines are dropped on save. Recommended: 3, 6 or 9 posts.
        </p>
        <Textarea
          data-testid="instagram-posts"
          value={instaPostsText}
          onChange={(e) => setInstaPostsText(e.target.value)}
          rows={6}
          placeholder={"https://www.instagram.com/p/CxYz123abc/\nhttps://www.instagram.com/reel/CxYz456def/"}
          className="rounded-none bg-zinc-950 border-zinc-800 text-white font-mono text-sm focus-visible:ring-orange-600"
        />
        <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
          Tip: on Instagram, open a post → click the ⋯ menu → "Copy link".
        </p>
      </div>

      {/* GALLERY MANAGER */}
      <div className="hairline bg-zinc-900 p-5 space-y-3 mt-6" data-testid="gallery-admin">
        <h3 className="font-display text-xl text-white tracking-wider">HOMEPAGE PHOTO SLIDESHOW</h3>
        <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
          Upload photos directly from your phone or paste a URL. Auto-advances every 5 sec. Order matters — top shows first.
        </p>

        {/* File upload button */}
        <div className="hairline bg-zinc-950 border-zinc-800 border-dashed p-4 text-center">
          <input
            data-testid="gallery-file-input"
            type="file"
            accept="image/*"
            multiple
            id="gallery-file-input"
            className="hidden"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              const next = [...(s.gallery_images || [])];
              let skipped = 0;
              for (const f of files) {
                if (f.size > 20 * 1024 * 1024) { skipped += 1; continue; }
                try {
                  const { dataUrl } = await compressImage(f, { maxWidth: 2200, quality: 0.88 });
                  next.push({ url: dataUrl, caption: "" });
                } catch {
                  skipped += 1;
                }
              }
              setS({ ...s, gallery_images: next });
              if (skipped) toast.error(`${skipped} photo(s) over 20MB were skipped`);
              else toast.success(`${files.length} photo(s) added`);
              e.target.value = "";
            }}
          />
          <label
            htmlFor="gallery-file-input"
            className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 hairline bg-orange-600 border-orange-500 text-white font-display tracking-wider text-xs hover:bg-orange-500 transition"
            data-testid="gallery-upload-trigger"
          >
            <ImagePlus size={14} /> UPLOAD FROM DEVICE
          </label>
          <p className="font-mono text-[9px] text-zinc-500 mt-2 tracking-widest uppercase">
            JPG / PNG · up to 20 MB each · multiple files supported
          </p>
        </div>

        {/* OR paste URL */}
        <p className="font-mono text-[9px] text-zinc-500 tracking-widest uppercase text-center">— OR PASTE AN IMAGE URL —</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input
            data-testid="gallery-url-input"
            value={galleryDraft.url}
            onChange={(e) => setGalleryDraft({ ...galleryDraft, url: e.target.value })}
            placeholder="https://images.example.com/photo.jpg"
            className="sm:col-span-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 font-mono text-xs"
          />
          <Input
            data-testid="gallery-caption-input"
            value={galleryDraft.caption}
            onChange={(e) => setGalleryDraft({ ...galleryDraft, caption: e.target.value })}
            placeholder="Caption (optional)"
            className="rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 text-sm"
          />
        </div>
        <Button
          data-testid="gallery-add-btn"
          onClick={addGalleryImage}
          variant="outline"
          className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider"
        >
          + ADD FROM URL
        </Button>
        {(s.gallery_images || []).length === 0 ? (
          <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
            No photos yet. Add a few to bring the homepage alive.
          </p>
        ) : (
          <div className="space-y-2">
            {s.gallery_images.map((g, i) => (
              <div key={i} className="hairline bg-zinc-950 border-zinc-800 p-2 flex items-center gap-3" data-testid={`gallery-row-${i}`}>
                <img src={g.url} alt="" className="w-16 h-12 object-cover hairline border-zinc-800" />
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] text-orange-500 truncate">{g.url}</p>
                  {g.caption && <p className="text-xs text-zinc-300 truncate">{g.caption}</p>}
                </div>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button onClick={() => moveGalleryImage(i, -1)} className="font-mono text-[10px] text-zinc-400 hover:text-orange-500 px-1" data-testid={`gallery-up-${i}`}>▲</button>
                  <button onClick={() => moveGalleryImage(i, 1)} className="font-mono text-[10px] text-zinc-400 hover:text-orange-500 px-1" data-testid={`gallery-down-${i}`}>▼</button>
                </div>
                <button
                  onClick={() => removeGalleryImage(i)}
                  className="text-zinc-500 hover:text-red-500 shrink-0"
                  data-testid={`gallery-remove-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="hairline bg-zinc-900 p-5 space-y-3 mt-6">
        <h3 className="font-display text-xl text-white tracking-wider">SOCIAL & COMMUNITY LINKS</h3>
        <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Shown in the footer of every page. Leave blank to hide.</p>
        <FieldInput label="OSCR INSTAGRAM" value={s.instagram_oscr} onChange={(v) => setS({ ...s, instagram_oscr: v })} dt="ins-oscr" placeholder="https://www.instagram.com/odishasteelcityrunners" />
        <FieldInput label="FOUNDER INSTAGRAM" value={s.instagram_founder} onChange={(v) => setS({ ...s, instagram_founder: v })} dt="ins-founder" placeholder="https://www.instagram.com/sumitsinghultrarunner" />
        <FieldInput label="WHATSAPP GROUP / CHAT LINK" value={s.whatsapp_url} onChange={(v) => setS({ ...s, whatsapp_url: v })} dt="whatsapp" placeholder="https://chat.whatsapp.com/..." />
        <FieldInput label="STRAVA CLUB LINK" value={s.strava_club_url} onChange={(v) => setS({ ...s, strava_club_url: v })} dt="strava" placeholder="https://www.strava.com/clubs/..." />
        <FieldInput label="YOUTUBE CHANNEL (optional)" value={s.youtube_url} onChange={(v) => setS({ ...s, youtube_url: v })} dt="youtube" placeholder="https://youtube.com/@..." />
      </div>

      <Button data-testid="settings-save-btn" onClick={save} className="mt-6 rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider w-full py-5">SAVE ALL SETTINGS</Button>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="hairline bg-zinc-900 p-3">
      <div className="font-display text-xl text-white">{value}</div>
      <div className="font-mono text-[10px] text-zinc-500 tracking-[0.25em] mt-1">{label}</div>
    </div>
  );
}

function EventsAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", description: "", date: "", location: "", distance_km: "", image: "" });
  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.title || !f.date) { toast.error("Title & date required"); return; }
    try {
      await api.post("/events", { ...f, distance_km: f.distance_km ? Number(f.distance_km) : null });
      setF({ title: "", description: "", date: "", location: "", distance_km: "", image: "" });
      load(); toast.success("Event created");
    } catch (e) { toast.error("Create failed"); }
  };
  const del = async (id) => { await api.delete(`/events/${id}`); load(); };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="hairline bg-zinc-900 p-5 space-y-3">
        <h3 className="font-display text-xl text-white tracking-wider">NEW EVENT</h3>
        <FieldInput label="TITLE" value={f.title} onChange={(v) => setF({ ...f, title: v })} dt="event-title" />
        <FieldTextarea label="DESCRIPTION" value={f.description} onChange={(v) => setF({ ...f, description: v })} dt="event-desc" />
        <FieldInput type="datetime-local" label="DATE & TIME" value={f.date} onChange={(v) => setF({ ...f, date: v })} dt="event-date" />
        <FieldInput label="LOCATION" value={f.location} onChange={(v) => setF({ ...f, location: v })} dt="event-location" />
        <FieldInput type="number" label="DISTANCE (KM)" value={f.distance_km} onChange={(v) => setF({ ...f, distance_km: v })} dt="event-distance" />
        <FieldInput label="IMAGE URL (optional)" value={f.image} onChange={(v) => setF({ ...f, image: v })} dt="event-image" />
        <Button data-testid="event-create-btn" onClick={create} className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider w-full">CREATE EVENT</Button>
      </div>
      <div className="space-y-2">
        {items.map((e) => (
          <div key={e.event_id} className="hairline bg-zinc-900 p-4 flex justify-between items-center">
            <div>
              <p className="font-mono text-[10px] tracking-widest text-orange-500 uppercase">{new Date(e.date).toLocaleString()}</p>
              <h4 className="font-display text-lg text-white">{e.title}</h4>
              <p className="text-xs text-zinc-400">{e.location} · {e.rsvps?.length || 0} going</p>
            </div>
            <button onClick={() => del(e.event_id)} className="text-zinc-500 hover:text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RacesAdmin() {
  const [items, setItems] = useState([]);
  const empty = {
    title: "", description: "", date: "", location: "",
    organiser: "", distances: "", registration_url: "", entry_fee: "", image: "", pitch: "", map_url: "",
  };
  const [f, setF] = useState(empty);
  const load = () => api.get("/races").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.title || !f.date || !f.location) {
      toast.error("Title, date and location are required");
      return;
    }
    try {
      await api.post("/races", f);
      setF(empty);
      load();
      toast.success("Race added");
    } catch { toast.error("Create failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this race?")) return;
    await api.delete(`/races/${id}`);
    load();
    toast.success("Race removed");
  };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="races-admin">
      <div className="hairline bg-zinc-900 p-5 space-y-3">
        <h3 className="font-display text-xl text-white tracking-wider">NEW RACE</h3>
        <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
          Third-party marathons, half marathons, ultras or trail runs OSCR is promoting.
        </p>
        <FieldInput label="TITLE" value={f.title} onChange={(v) => setF({ ...f, title: v })} dt="race-title" />
        <FieldTextarea label="DESCRIPTION" value={f.description} onChange={(v) => setF({ ...f, description: v })} dt="race-desc" />
        <FieldInput type="datetime-local" label="RACE DATE / START TIME" value={f.date} onChange={(v) => setF({ ...f, date: v })} dt="race-date" />
        <FieldInput label="LOCATION (e.g. Mumbai, India)" value={f.location} onChange={(v) => setF({ ...f, location: v })} dt="race-location" />
        <FieldInput label="ORGANISER (optional)" value={f.organiser} onChange={(v) => setF({ ...f, organiser: v })} dt="race-organiser" />
        <FieldInput label="DISTANCES (e.g. 10K · Half · Full)" value={f.distances} onChange={(v) => setF({ ...f, distances: v })} dt="race-distances" />
        <FieldInput label="ENTRY FEE (e.g. ₹1,500)" value={f.entry_fee} onChange={(v) => setF({ ...f, entry_fee: v })} dt="race-fee" />
        <FieldInput label="REGISTRATION URL" value={f.registration_url} onChange={(v) => setF({ ...f, registration_url: v })} dt="race-reg-url" />
        <FieldInput label="COVER IMAGE URL (optional)" value={f.image} onChange={(v) => setF({ ...f, image: v })} dt="race-image" />
        <FieldInput label="VENUE MAP LINK (optional · Google Maps)" value={f.map_url} onChange={(v) => setF({ ...f, map_url: v })} dt="race-map-url" placeholder="https://maps.app.goo.gl/…" />
        <FieldTextarea label="JOIN-US PITCH · SHOWN ON RUNNERS' WELCOME BOARD (optional)" value={f.pitch} onChange={(v) => setF({ ...f, pitch: v })} dt="race-pitch" rows={2} />
        <Button data-testid="race-create-btn" onClick={create} className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider w-full">
          ADD RACE
        </Button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm">
            No races added yet.
          </div>
        )}
        {items.map((r) => (
          <div key={r.race_id} data-testid={`admin-race-row-${r.race_id}`} className="hairline bg-zinc-900 p-4 flex justify-between items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] tracking-widest text-orange-500 uppercase">
                {new Date(r.date).toLocaleString()}
              </p>
              <h4 className="font-display text-lg text-white truncate">{r.title}</h4>
              <p className="text-xs text-zinc-400 truncate">
                {r.location}{r.organiser ? ` · ${r.organiser}` : ""}{r.distances ? ` · ${r.distances}` : ""}
              </p>
              {r.registration_url && (
                <a href={r.registration_url} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-orange-500 hover:underline break-all">Registration link</a>
              )}
              <RacePitchEditor race={r} onSaved={load} />
              <RaceRosterDialog race={r} />
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 mt-2" data-testid={`admin-race-joiners-${r.race_id}`}>
                <span className="text-white">{r.joiners_count || 0}</span> joining
                {(r.joiners || []).length > 0 && (
                  <span className="text-zinc-500 normal-case tracking-normal"> · {r.joiners.map((u) => `${u.name}${u.city ? ` (${u.city})` : ""}`).join(", ")}</span>
                )}
              </p>
            </div>
            <button onClick={() => del(r.race_id)} className="text-zinc-500 hover:text-red-500 shrink-0" title="Delete race">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


function ChallengesAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", description: "", start_date: "", end_date: "", target_days: "", target_km_per_day: "", image: "", activity_type: "any" });
  const load = () => api.get("/challenges").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.title || !f.start_date || !f.end_date) { toast.error("Required fields missing"); return; }
    try {
      await api.post("/challenges", {
        ...f,
        target_days: Number(f.target_days) || 0,
        target_km_per_day: f.target_km_per_day ? Number(f.target_km_per_day) : null,
      });
      setF({ title: "", description: "", start_date: "", end_date: "", target_days: "", target_km_per_day: "", image: "", activity_type: "any" });
      load(); toast.success("Challenge created");
    } catch { toast.error("Create failed"); }
  };
  const del = async (id) => { await api.delete(`/challenges/${id}`); load(); };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="hairline bg-zinc-900 p-5 space-y-3">
        <h3 className="font-display text-xl text-white tracking-wider">NEW CHALLENGE</h3>
        <FieldInput label="TITLE" value={f.title} onChange={(v) => setF({ ...f, title: v })} dt="ch-title" />
        <FieldTextarea label="DESCRIPTION" value={f.description} onChange={(v) => setF({ ...f, description: v })} dt="ch-desc" />
        <div className="grid grid-cols-2 gap-3">
          <FieldInput type="date" label="START" value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} dt="ch-start" />
          <FieldInput type="date" label="END" value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} dt="ch-end" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FieldInput type="number" label="TARGET DAYS" value={f.target_days} onChange={(v) => setF({ ...f, target_days: v })} dt="ch-days" />
          <FieldInput type="number" label="KM / DAY (opt)" value={f.target_km_per_day} onChange={(v) => setF({ ...f, target_km_per_day: v })} dt="ch-km" />
        </div>
        <div>
          <label className="font-display tracking-widest text-xs text-zinc-400 uppercase">ACTIVITY TYPE</label>
          <select
            data-testid="ch-activity-type"
            value={f.activity_type}
            onChange={(e) => setF({ ...f, activity_type: e.target.value })}
            className="mt-2 w-full rounded-none bg-zinc-950 border border-zinc-800 text-white px-3 py-2 focus:ring-orange-600 focus:outline-none focus:border-orange-600"
          >
            <option value="any">ANY ACTIVITY (run + walk + cycle)</option>
            <option value="run">RUNNING ONLY</option>
            <option value="walk">WALKING ONLY</option>
            <option value="cycle">CYCLING ONLY</option>
          </select>
          <p className="font-mono text-[10px] text-zinc-500 mt-1 tracking-widest uppercase">
            Restricts the challenge leaderboard to activities of this type
          </p>
        </div>
        <FieldInput label="IMAGE URL (optional)" value={f.image} onChange={(v) => setF({ ...f, image: v })} dt="ch-image" />
        <Button data-testid="ch-create-btn" onClick={create} className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider w-full">CREATE CHALLENGE</Button>
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.challenge_id} className="hairline bg-zinc-900 p-4 flex justify-between items-center">
            <div>
              <p className="font-mono text-[10px] tracking-widest text-orange-500 uppercase">{c.start_date} → {c.end_date}</p>
              <h4 className="font-display text-lg text-white">{c.title}</h4>
              {c.activity_type && c.activity_type !== "any" && (
                <span className="inline-block mt-1 px-2 py-0.5 hairline bg-orange-600/10 border-orange-600/40 font-mono text-[9px] tracking-widest text-orange-500 uppercase">
                  {c.activity_type} ONLY
                </span>
              )}
            </div>
            <button onClick={() => del(c.challenge_id)} className="text-zinc-500 hover:text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TipsAdmin() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", content: "", image: "" });
  const load = () => api.get("/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.title || !f.content) { toast.error("Title & content required"); return; }
    try {
      await api.post("/blog", f);
      setF({ title: "", content: "", image: "" });
      load(); toast.success("Published");
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/blog/${id}`); load(); };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="hairline bg-zinc-900 p-5 space-y-3">
        <h3 className="font-display text-xl text-white tracking-wider">NEW TIP / BLOG POST</h3>
        <FieldInput label="TITLE" value={f.title} onChange={(v) => setF({ ...f, title: v })} dt="tip-title" />
        <FieldTextarea label="CONTENT" rows={8} value={f.content} onChange={(v) => setF({ ...f, content: v })} dt="tip-content" />
        <FieldInput label="IMAGE URL (optional)" value={f.image} onChange={(v) => setF({ ...f, image: v })} dt="tip-image" />
        <Button data-testid="tip-create-btn" onClick={create} className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider w-full">PUBLISH</Button>
      </div>
      <div className="space-y-2">
        {items.map((b) => (
          <div key={b.blog_id} className="hairline bg-zinc-900 p-4 flex justify-between items-center">
            <div>
              <p className="font-mono text-[10px] tracking-widest text-orange-500 uppercase">{new Date(b.created_at).toDateString()}</p>
              <h4 className="font-display text-lg text-white">{b.title}</h4>
            </div>
            <button onClick={() => del(b.blog_id)} className="text-zinc-500 hover:text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", dt, placeholder }) {
  return (
    <div>
      <Label className="font-display tracking-widest text-xs text-zinc-400">{label}</Label>
      <Input data-testid={dt} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
    </div>
  );
}

function FieldTextarea({ label, value, onChange, dt, rows = 3 }) {
  return (
    <div>
      <Label className="font-display tracking-widest text-xs text-zinc-400">{label}</Label>
      <Textarea data-testid={dt} value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
        className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600" />
    </div>
  );
}
